import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import { LocalStorageKeys } from '@pkg/types';

import { Users } from '@app/client';
import { Request } from '@app/client/common/api';
import {
    type ApiResult,
    type BaseParams,
    type ConsumeUser,
    type DiscordUserResponse,
    type InitializeUser,
    type ListUsersResponse,
    type MyOrdersResponse,
    type OptinUser,
    type Order,
    type PostParams,
    type RegisterUser,
    type Token,
    type TwitterUserResponse,
    type UpdateUsernameParams,
    type UpdateUserParameters,
    type UserAccount,
} from '@app/client/common/structs';
import { type OptInType, type PrimaryYatParams } from '@app/client/common/structs/user/user';
import { links } from '@app/client/links';
import { prefetch2FA } from '@app/helpers';
import { type RootState } from '@app/store/reducers';

import { apiReturnResults } from '../../ApiFactory';

export async function initializeUser(params: BaseParams) {
    return Request.getRequest<InitializeUser>({
        ...params,
        target: links.user().load(),
    });
}

export async function getCurrentUser(params: BaseParams) {
    return Request.getRequest<UserAccount>({
        ...params,
        target: links.user().current(),
    });
}

export interface UpdateUserResponseValue extends UserAccount {
    message: string;
    status: string;
    token_response: Token;
}

export async function updateCurrentUser(params: BaseParams & PostParams<UpdateUserParameters>) {
    return Request.patchRequest<UserAccount>({
        ...params,
        target: links.user().current(),
        settings: {
            ...params.settings,
        },
    });
}

type UpdatePrimaryYatParams = BaseParams & PostParams<PrimaryYatParams>;
export async function updatePrimaryYat(params: UpdatePrimaryYatParams) {
    return Request.patchRequest({
        ...params,
        target: links.user().primaryYat(),
    });
}

export async function updateUsername(params: BaseParams & PostParams<UpdateUsernameParams>) {
    return Request.patchRequest<UserAccount>({
        ...params,
        target: links.user().username(),
    });
}

export const callUpdateUsername = (params: BaseParams & PostParams<UpdateUsernameParams>) =>
    apiReturnResults<UserAccount>(updateUsername, params);

export const callUpdateCurrentUser = (
    params: BaseParams & PostParams<UpdateUserParameters>,
    dispatch?: ThunkDispatch<RootState, void, Action>,
) =>
    apiReturnResults<UpdateUserResponseValue>(updateCurrentUser, {
        prefetch: dispatch ? prefetch2FA(dispatch) : undefined,
        ...params,
    });

export async function registerEmptyUser(params: BaseParams & PostParams<RegisterUser>) {
    return Request.postRequest<Token>({
        ...params,
        target: links.user().collection(),
        settings: {
            ...params.settings,
            credentials: 'omit',
        },
    });
}

export async function consumeDraftUser(params: BaseParams & PostParams<ConsumeUser>) {
    localStorage.setItem(LocalStorageKeys.RemoveDraftUser, '1');
    return Request.postRequest<Order>({
        ...params,
        target: links.user().consume(params.value.userId),
        settings: {
            ...params.settings,
            credentials: 'include',
        },
    });
}

export async function getMyOrders(params: BaseParams & PostParams) {
    return Request.getRequest<MyOrdersResponse>({
        ...params,
        target: links.user().orders(),
        settings: {
            ...params.settings,
        },
    });
}

export async function getUserDiscordData(params: BaseParams) {
    return Request.getRequest<DiscordUserResponse>({
        ...params,
        target: links.user().discordData(),
        settings: {
            ...params.settings,
        },
    });
}

type WaitListType = 'Discord' | 'Yat';

export async function getUserWaitListSize(params: BaseParams & { waitlistType: WaitListType }) {
    return Request.getRequest<number>({
        ...params,
        target: links.user().waitlistSize(params.waitlistType),
        settings: {
            ...params.settings,
        },
    });
}

export async function getUserWaitListPlace(params: BaseParams & { waitlistType: WaitListType }) {
    return Request.getRequest<number>({
        ...params,
        target: links.user().waitlistPlace(params.waitlistType),
        settings: {
            ...params.settings,
        },
    });
}

interface RegisterUserResponse {
    id: string;
    user_id: string;
    rank_place: number;
    waitlist_type: WaitListType;
    created_at: Date;
}
export async function registerUserIntoWaitlist(params: BaseParams & { waitlistType: WaitListType } & PostParams) {
    return Request.postRequest<RegisterUserResponse>({
        ...params,
        target: links.user().waitlist(params.waitlistType),
        settings: {
            ...params.settings,
        },
    });
}

export async function getUserTwitterData(params: BaseParams) {
    return Request.getRequest<TwitterUserResponse>({
        ...params,
        target: links.user().twitterData(),
        settings: {
            ...params.settings,
        },
    });
}

export async function removeUserTwitterData(params: BaseParams) {
    return Request.deleteRequest({
        ...params,
        target: links.auth().twitterDeLink(),
    });
}

export async function listUsers({
    twitterHandle,
    primaryYat,
    ...params
}: BaseParams & { primaryYat?: string; twitterHandle?: string }): Promise<ApiResult<ListUsersResponse>> {
    const query = { ...params.query };
    if (twitterHandle) query.twitter_handle = twitterHandle;
    if (primaryYat) query.primary_yat = primaryYat;
    return Request.getRequest<ListUsersResponse>({
        ...params,
        target: links.userV2().collection(),
        settings: {
            ...params.settings,
        },
        query,
    });
}

export async function optInUser(params: BaseParams & { optInType: OptInType; token?: string } & PostParams<OptinUser>) {
    return Request.postRequest<OptinUser>({
        ...params,
        target: links.user().optIn(params.optInType),
        settings: {
            ...params.settings,
        },
    });
}

export interface UpdateOptInUserResponse {
    id: string;
    user_id: string;
    opt_in_id: string;
    opted_in: boolean;
    created_at: Date;
    updated_at: Date;
}

export const callOptInUser = (
    params: BaseParams & { optInType: OptInType } & PostParams<OptinUser>,
    accessToken?: string,
) => apiReturnResults<UpdateOptInUserResponse>(optInUser, params, false, accessToken);

export const callInitializeUser = (params: BaseParams) =>
    apiReturnResults<InitializeUser>(Users.initializeUser, params);

export const callConsumerDraftUser = (params: BaseParams & PostParams<ConsumeUser>) =>
    apiReturnResults<Order>(Users.consumeDraftUser, params);

export const callUpdatePrimaryYat = (params: UpdatePrimaryYatParams) => apiReturnResults(updatePrimaryYat, params);
